import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Collapse
} from '@mui/material';
import { Warning, Error, Info } from '@mui/icons-material';
import { leaveService } from '../../services/api';
import { useTranslation } from 'react-i18next';

const LeaveRequestValidation = ({
  leaveTypeId,
  startDate,
  businessDays,
  excludeRequestId = null,
  onValidationChange
}) => {
  const { t } = useTranslation();
  const [validation, setValidation] = useState(null);
  const [overlapping, setOverlapping] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  // Stable primitive to avoid re-validation loops when parent re-creates Date
  const startIso = startDate ? startDate.toISOString() : null;

  useEffect(() => {
    if (leaveTypeId && startIso && businessDays > 0) {
      validateRequest();
    } else {
      setValidation(null);
      setOverlapping([]);
      onValidationChange?.(null);
    }
  }, [leaveTypeId, startIso, businessDays, excludeRequestId]);

  const validateRequest = async () => {
    setLoading(true);
    try {
      // Run both validation and overlap check in parallel
      const [validationResponse, overlapResponse] = await Promise.all([
        leaveService.validateRequest({
          leaveTypeId,
          startDateUtc: startIso,
          businessDays,
          reason: 'Validation check'
        }),
        leaveService.checkOverlap(startDate, businessDays, excludeRequestId)
      ]);

      const [validationData, overlapData] = [validationResponse, overlapResponse];

      setValidation(validationData);
      setOverlapping(overlapData.overlapping || []);
      
      // Notify parent component
      onValidationChange?.({
        isValid: validationData.isValid,
        hasErrors: validationData.errors?.length > 0,
        hasWarnings: validationData.warnings?.length > 0,
        hasOverlaps: overlapData.overlapping?.length > 0,
        errors: validationData.errors || [],
        warnings: validationData.warnings || [],
        overlapping: overlapData.overlapping || []
      });

    } catch (error) {
      console.error('Validation failed:', error);
      const errorData = {
        isValid: false,
        hasErrors: true,
        hasWarnings: false,
        hasOverlaps: false,
        errors: [{ code: 'VALIDATION_ERROR', message: 'Validation check failed. Please try again.' }],
        warnings: [],
        overlapping: []
      };
      setValidation(errorData);
      onValidationChange?.(errorData);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          {t('leave.validation.validating')}
        </Typography>
      </Box>
    );
  }

  if (!validation) {
    return null;
  }

  const hasErrors = validation.errors?.length > 0;
  const hasWarnings = validation.warnings?.length > 0;
  const hasOverlaps = overlapping.length > 0;

  return (
    <Box sx={{ my: 2 }}>
      {/* Overlap Warning */}
      {hasOverlaps && (
        <Alert 
          severity="warning" 
          sx={{ mb: 1 }}
          action={
            <Chip
              aria-label={t('leave.validation.toggleOverlapDetails')}
              label={t(overlapping.length === 1 ? 'leave.validation.conflicts_one' : 'leave.validation.conflicts_other', { count: overlapping.length })}
              size="small"
              color="warning"
              onClick={() => setShowDetails(!showDetails)}
              sx={{ cursor: 'pointer' }}
            />
          }
        >
          <AlertTitle>{t('leave.validation.overlapTitle')}</AlertTitle>
          <Typography variant="body2">
            {t('leave.validation.overlapHint')}
          </Typography>
          
          <Collapse in={showDetails}>
            <List dense sx={{ mt: 1 }}>
              {overlapping.map((req, index) => (
                <ListItem key={req.id} sx={{ pl: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {req.startDate} - {req.endDate}
                        </Typography>
                        <Chip 
                          label={req.status} 
                          size="small" 
                          color={req.status === 'Approved' ? 'success' : 'default'}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {req.businessDays} days • {req.leaveType}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Alert>
      )}

      {/* Validation Errors */}
      {hasErrors && (
        <Alert severity="error" sx={{ mb: 1 }} icon={<Error />}>
          <AlertTitle>{t('leave.validation.cannotSubmit')}</AlertTitle>
          {validation.errors.map((error, index) => (
            <Box key={index} sx={{ mt: index > 0 ? 1 : 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {getErrorTitle(error.code, t)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {getHumanReadableMessage(error, t)}
              </Typography>
            </Box>
          ))}
        </Alert>
      )}

      {/* Validation Warnings */}
      {hasWarnings && (
        <Alert severity="warning" sx={{ mb: 1 }} icon={<Warning />}>
          <AlertTitle>{t('leave.validation.warningsTitle')}</AlertTitle>
          {validation.warnings.map((warning, index) => (
            <Box key={index} sx={{ mt: index > 0 ? 1 : 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {getWarningTitle(warning.code, t)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {getHumanReadableMessage(warning, t)}
              </Typography>
            </Box>
          ))}
        </Alert>
      )}

      {/* Success State */}
      {validation.isValid && !hasWarnings && !hasOverlaps && (
        <Alert severity="success" sx={{ mb: 1 }} icon={<Info />}>
          <Typography variant="body2">
            {t('leave.validation.successProceed')}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

// Helper functions for human-readable messages
const getErrorTitle = (code, t) => {

  const key = `leave.validation.errors.${code}`;
  const val = t(key);
  return (val && val !== key) ? `❌ ${val}` : '❌ Error';
};

const getWarningTitle = (code, t) => {

  const key = `leave.validation.warnings.${code}`;
  const val = t(key);
  return (val && val !== key) ? `⚠️ ${val}` : '⚠️ Warning';
};

const getHumanReadableMessage = (item, t) => {
  const { code, message } = item;
  const m = String(message || '');
  switch (code) {
    case 'INSUFFICIENT_BALANCE': {
      const mm = m.match(/Requested: (\d+) days, Available: (\d+) days/);
      return t('leave.validation.messages.INSUFFICIENT_BALANCE', {
        requested: mm ? mm[1] : undefined,
        available: mm ? mm[2] : undefined,
      });
    }
    case 'OVERLAP':
      return t('leave.validation.messages.OVERLAP');
    case 'PAST_DATE':
      return t('leave.validation.messages.PAST_DATE');
    case 'SHORT_NOTICE': {
      const mm = m.match(/(\d+) days in advance/);
      return t('leave.validation.messages.SHORT_NOTICE', { days: mm ? mm[1] : undefined });
    }
    case 'TOO_CLOSE':
      return t('leave.validation.messages.TOO_CLOSE');
    case 'HIGH_USAGE': {
      const mm = m.match(/(\d+) of your (\d+) available days/);
      return t('leave.validation.messages.HIGH_USAGE', {
        used: mm ? mm[1] : undefined,
        available: mm ? mm[2] : undefined,
        remaining: mm ? (Number(mm[2]) - Number(mm[1])) : undefined,
      });
    }
    case 'LARGE_REQUEST': {
      const mm = m.match(/(\d+) days at once/);
      return t('leave.validation.messages.LARGE_REQUEST', { days: mm ? mm[1] : undefined });
    }
    case 'TOO_MANY_DAYS': {
      const mm = m.match(/(\d+) consecutive business days/);
      return t('leave.validation.messages.TOO_MANY_DAYS', { max: mm ? mm[1] : undefined });
    }
    case 'INVALID_DAYS':
      return t('leave.validation.messages.INVALID_DAYS');
    case 'EMPLOYEE_INACTIVE':
      return t('leave.validation.messages.EMPLOYEE_INACTIVE');
    case 'INVALID_LEAVE_TYPE':
      return t('leave.validation.messages.INVALID_LEAVE_TYPE');
    case 'NO_BALANCE': {
      const mm = m.match(/(\d{4})/);
      return t('leave.validation.messages.NO_BALANCE', { year: mm ? mm[1] : undefined });
    }
    default:
      return t('leave.validation.messages.VALIDATION_ERROR');
  }
};

export default LeaveRequestValidation;
